import { type ChangeUrlDecision, type ChangeUrlToolResult } from 'shared/copilot/tools/changeUrl'
import {
  type NavigateToProjectDecision,
  type NavigateToProjectToolResult
} from 'src/creativeAssets/copilot/toolLogic/startPageToolLogic'

export interface ChangeUrlArgs {
  targetId: string | undefined
  replace: boolean
}

export type HitlNavigationProject = NonNullable<NavigateToProjectToolResult['project']>

/**
 * Coerces `change_url` tool-call arguments into card-ready fields.
 */
export const parseChangeUrlArgs = (args: Record<string, unknown> | null | undefined): ChangeUrlArgs => {
  if (args == null || typeof args !== 'object') {
    return { targetId: undefined, replace: false }
  }

  const targetId = typeof args.targetId === 'string' ? args.targetId : undefined
  return {
    targetId,
    replace: args.replace === true
  }
}

const parseHitlStatus = (statusRaw: string | null, success: unknown): ChangeUrlToolResult['status'] | null => {
  if (statusRaw === 'approved' || statusRaw === 'rejected' || statusRaw === 'pending' || statusRaw === 'failed') {
    return statusRaw
  }

  // Pre-HITL change_url results navigated immediately and only set `success`.
  if (success === true) return 'approved'
  if (success === false) return 'failed'
  return null
}

const parseProjectRecord = (record: Record<string, unknown>): HitlNavigationProject | null => {
  const projectRecord =
    record.project != null && typeof record.project === 'object' && !Array.isArray(record.project)
      ? (record.project as Record<string, unknown>)
      : null

  if (
    projectRecord == null ||
    typeof projectRecord.id !== 'number' ||
    typeof projectRecord.title !== 'string' ||
    typeof projectRecord.path !== 'string' ||
    typeof projectRecord.tab !== 'string'
  ) {
    return null
  }

  return {
    id: projectRecord.id,
    title: projectRecord.title,
    path: projectRecord.path,
    tab: projectRecord.tab
  }
}

const parseTargetRecord = (record: Record<string, unknown>): NonNullable<ChangeUrlToolResult['target']> | null => {
  const targetRecord =
    record.target != null && typeof record.target === 'object' && !Array.isArray(record.target)
      ? (record.target as Record<string, unknown>)
      : null

  if (
    targetRecord == null ||
    typeof targetRecord.id !== 'string' ||
    typeof targetRecord.label !== 'string' ||
    typeof targetRecord.path !== 'string'
  ) {
    return null
  }

  return { id: targetRecord.id, label: targetRecord.label, path: targetRecord.path }
}

/**
 * Reads decision / target from a HITL tool result payload (JSON string or object).
 * Also accepts `navigate_to_project` payloads that nest the destination under `project`.
 * Legacy auto-navigation results (success without `status`) are treated as approved.
 */
export const parseChangeUrlResult = (
  content: unknown
): {
  status: ChangeUrlToolResult['status'] | null
  target: ChangeUrlToolResult['target'] | null
  project: HitlNavigationProject | null
  replace: boolean
} => {
  let parsed: unknown = content
  if (typeof content === 'string') {
    try {
      parsed = JSON.parse(content)
    } catch {
      return { status: null, target: null, project: null, replace: false }
    }
  }

  if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { status: null, target: null, project: null, replace: false }
  }

  const record = parsed as Record<string, unknown>
  const statusRaw = typeof record.status === 'string' ? record.status : null
  const status = parseHitlStatus(statusRaw, record.success)
  const project = parseProjectRecord(record)
  const targetFromRecord = parseTargetRecord(record)
  const target =
    targetFromRecord ?? (project != null ? { id: String(project.id), label: project.title, path: project.path } : null)

  return {
    status,
    target,
    project,
    replace: record.replace === true
  }
}

/**
 * Builds the tool result payload written back when the user decides on `change_url`.
 */
export const buildChangeUrlDecisionResult = ({
  decision,
  target,
  replace
}: {
  decision: ChangeUrlDecision
  target: NonNullable<ChangeUrlToolResult['target']>
  replace: boolean
}): ChangeUrlToolResult => {
  if (decision === 'approved') {
    return {
      success: true,
      status: 'approved',
      message: `Navigated to ${target.label}.`,
      replace,
      target
    }
  }

  return {
    success: false,
    status: 'rejected',
    message: `User declined navigation to ${target.label}.`,
    replace,
    target
  }
}

/**
 * Builds the tool result payload written back when the user decides on `navigate_to_project`.
 */
export const buildNavigateToProjectDecisionResult = ({
  decision,
  project,
  replace
}: {
  decision: NavigateToProjectDecision
  project: HitlNavigationProject
  replace: boolean
}): NavigateToProjectToolResult => {
  if (decision === 'approved') {
    return {
      success: true,
      status: 'approved',
      message: `Opened project "${project.title}".`,
      replace,
      project
    }
  }

  return {
    success: false,
    status: 'rejected',
    message: `User declined opening project "${project.title}".`,
    replace,
    project
  }
}
